import React, { useState, useEffect } from "react";
import styles from "./MyLatestOrderTile.module.scss";
import classnames from "classnames";
import Tile from "@src/components/containers/Tile";
import ProgressBar from "@src/components/common/ProgressBar";
import { Link } from "react-router-dom";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import * as OrderApi from "@src/api/OrderApi";
import SpinningLoader from "@src/components/common/SpinningLoader";
import moment from "moment";
import filter from "lodash/filter";
import OrderStatuses, { OrderStatusLabels } from "@src/constants/OrderStatuses";
import DistributorLogo from "@src/components/common/DistributorLogo";
import { ProductRecommendationTypes } from "../ProductRecommendations";

interface OrderDetail {
  distributorSlug: string;
  status: string;
}

interface LatestOrder {
  id: string;
  placedAtTimestamp: string;
  orderNumber: string;
  distributorProductGroupings: OrderDetail[];
  itemString: string;
}

export default function MyLatestOrderTile() {
  const { showToast } = useToast();
  const [latestOrder, setLatestOrder] = useState<LatestOrder | null>(null);
  const [isLoadingLatestOrder, setIsLoadingLatestOrder] = useState(false);
  const completedOrders = filter(latestOrder?.distributorProductGroupings, {
    status: OrderStatuses.Delivered,
  });
  const distributorProductGroupingsCount =
    latestOrder?.distributorProductGroupings?.length || 0;
  const distributorProductGroupingsPercentDelivered =
    ((completedOrders.length || 1) / distributorProductGroupingsCount) * 100;

  useEffect(() => {
    handleLoadLatestOrder();
  }, []);

  const handleLoadLatestOrder = async () => {
    setIsLoadingLatestOrder(true);
    try {
      const response = await OrderApi.getLatestOrder();
      setLatestOrder(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading product categories",
        variant: "error",
      });
    } finally {
      setIsLoadingLatestOrder(false);
    }
  };

  return (
    <Tile>
      <div className="spaced-content-v-lg">
        {isLoadingLatestOrder && <SpinningLoader />}

        {!!latestOrder && Object.keys(latestOrder).length > 0 ? (
          <>
            <div className="flex-row space-between">
              <div className={styles.header}>My Latest Order</div>

              <div className={styles.orderProgressContainer}>
                <ProgressBar
                  percentage={distributorProductGroupingsPercentDelivered}
                  segments={distributorProductGroupingsCount}
                />
              </div>
            </div>

            <div className="spaced-content-v-xs">
              <div className="flex-row flex-center spaced-content-lg">
                <span>{latestOrder.orderNumber}</span>
                <span className="text-sm">
                  Ordered{" "}
                  {moment(latestOrder.placedAtTimestamp).format("MM/DD")}
                </span>
              </div>
              <div>{latestOrder.itemString}</div>
            </div>

            <div className="border-top padding-top-lg spaced-content-v-lg">
              {latestOrder.distributorProductGroupings?.map(
                (distributorProductGrouping, index) => (
                  <div
                    key={`latest_order_detail_${index}`}
                    className="flex-row flex-center space-between"
                  >
                    <DistributorLogo
                      distributorSlug={
                        distributorProductGrouping.distributorSlug
                      }
                      height={20}
                    />

                    <div
                      className={classnames(
                        "text-sm",
                        distributorProductGrouping.status ===
                          OrderStatuses.Delivered
                          ? "text-success font-weight-bold text-italic"
                          : "text-light",
                      )}
                    >
                      {OrderStatusLabels[distributorProductGrouping.status]}
                    </div>
                  </div>
                ),
              )}
            </div>
          </>
        ) : (
          <div>No Orders</div>
        )}

        <div className="border-top-thick text-xs padding-top-sm">
          {/* @ts-ignore */}
          <Link id="latest-order-tile-view-all-orders-link" to="/orders">
            View All Orders
          </Link>
          <span className="font-weight-light">&nbsp;|&nbsp;</span>
          {/* @ts-ignore */}
          <Link
            id="latest-order-tile-view-previously-ordered-items-link"
            to={`/product-recommendations/${ProductRecommendationTypes.PastPurchases}`}
          >
            View Previously Ordered Items
          </Link>
        </div>
      </div>
    </Tile>
  );
}
